/**
 * Personalakte-Tab „Stammdaten": Edit-Formular der Kernfelder (PATCH /employees/:id).
 */

import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { api, apiError } from "@/services/api"
import type {
  Anrede,
  EmployeeDetail,
  EmployeeUpdate,
  Geschlecht,
  Mitarbeitertyp,
} from "@/types"
import { ANREDE_LABEL, GESCHLECHT_LABEL, MITARBEITERTYP_LABEL, toOptions } from "./labels"

interface FormState {
  salutation: string
  firstName: string
  lastName: string
  birthName: string
  birthDate: string
  gender: string
  employeeType: string
  disability: boolean
  profession: string
  hireDate: string
  terminationDate: string
  emergencyContactName: string
  notes: string
}

function fromDetail(d: EmployeeDetail): FormState {
  return {
    salutation: d.salutation ?? "",
    firstName: d.firstName,
    lastName: d.lastName,
    birthName: d.birthName ?? "",
    birthDate: d.birthDate ?? "",
    gender: d.gender,
    employeeType: d.employeeType ?? "",
    disability: d.disability,
    profession: d.profession ?? "",
    hireDate: d.hireDate ?? "",
    terminationDate: d.terminationDate ?? "",
    emergencyContactName: d.emergencyContactName ?? "",
    notes: d.notes ?? "",
  }
}

const nul = (v: string): string | null => (v.trim() === "" ? null : v.trim())

export function StammdatenTab({
  detail,
  canWrite,
}: {
  detail: EmployeeDetail
  canWrite: boolean
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<FormState>(() => fromDetail(detail))

  const save = useMutation({
    mutationFn: (body: EmployeeUpdate) => api.employees.update(detail.id, body),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["employee", detail.id] })
      queryClient.invalidateQueries({ queryKey: ["employees"] })
      setForm(fromDetail(updated))
      toast.success("Stammdaten gespeichert")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function submit() {
    save.mutate({
      salutation: (nul(form.salutation) as Anrede | null) ?? null,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      birthName: nul(form.birthName),
      birthDate: nul(form.birthDate),
      gender: form.gender as Geschlecht,
      employeeType: (nul(form.employeeType) as Mitarbeitertyp | null) ?? null,
      disability: form.disability,
      profession: nul(form.profession),
      hireDate: nul(form.hireDate),
      terminationDate: nul(form.terminationDate),
      emergencyContactName: nul(form.emergencyContactName),
      notes: nul(form.notes),
    })
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const valid = form.firstName.trim() !== "" && form.lastName.trim() !== ""
  const dis = !canWrite

  function field(label: string, node: React.ReactNode) {
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        {node}
      </div>
    )
  }

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {field(
            "Anrede",
            <Select
              value={form.salutation}
              disabled={dis}
              onChange={(e) => set("salutation", e.target.value)}
            >
              <option value="">— keine —</option>
              {toOptions(ANREDE_LABEL).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
          {field(
            "Vorname *",
            <Input
              value={form.firstName}
              disabled={dis}
              onChange={(e) => set("firstName", e.target.value)}
            />
          )}
          {field(
            "Nachname *",
            <Input
              value={form.lastName}
              disabled={dis}
              onChange={(e) => set("lastName", e.target.value)}
            />
          )}
          {field(
            "Geburtsname",
            <Input
              value={form.birthName}
              disabled={dis}
              onChange={(e) => set("birthName", e.target.value)}
            />
          )}
          {field(
            "Geburtsdatum",
            <Input
              type="date"
              value={form.birthDate}
              disabled={dis}
              onChange={(e) => set("birthDate", e.target.value)}
            />
          )}
          {field(
            "Geschlecht *",
            <Select
              value={form.gender}
              disabled={dis}
              onChange={(e) => set("gender", e.target.value)}
            >
              {toOptions(GESCHLECHT_LABEL).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
          {field(
            "Mitarbeitertyp",
            <Select
              value={form.employeeType}
              disabled={dis}
              onChange={(e) => set("employeeType", e.target.value)}
            >
              <option value="">— kein Typ —</option>
              {toOptions(MITARBEITERTYP_LABEL).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
          {field(
            "Beruf",
            <Input
              value={form.profession}
              disabled={dis}
              onChange={(e) => set("profession", e.target.value)}
            />
          )}
          <div className="flex items-center justify-between gap-2 pt-6">
            <Label htmlFor="disability">Behinderung</Label>
            <Switch
              id="disability"
              checked={form.disability}
              disabled={dis}
              onCheckedChange={(c) => set("disability", c)}
            />
          </div>
          {field(
            "Eintritt",
            <Input
              type="date"
              value={form.hireDate}
              disabled={dis}
              onChange={(e) => set("hireDate", e.target.value)}
            />
          )}
          {field(
            "Austritt",
            <Input
              type="date"
              value={form.terminationDate}
              disabled={dis}
              onChange={(e) => set("terminationDate", e.target.value)}
            />
          )}
          {field(
            "Notfallkontakt",
            <Input
              value={form.emergencyContactName}
              disabled={dis}
              placeholder="Name, Rufnummer"
              onChange={(e) => set("emergencyContactName", e.target.value)}
            />
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Bemerkungen</Label>
          <Textarea
            value={form.notes}
            disabled={dis}
            onChange={(e) => set("notes", e.target.value)}
          />
        </div>
        {canWrite && (
          <div className="flex justify-end">
            <Button disabled={!valid || save.isPending} onClick={submit}>
              Speichern
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
